import { styled, TextField, Box } from '@mui/material';
import type { SxProps, InputProps, TextFieldProps } from '@mui/material';
import type { ComponentType } from 'react';
import { AccessControl } from 'modules/accessControl/components/AccessControl';
import { CommonLabelSeparateProps } from './types';
import { forwardRef } from 'react';
import { getInputStyles } from './utils/getInputStyles';

export type TextInputProps = CommonLabelSeparateProps & {
  autoFocus?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  defaultValue?: string;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  id?: string;
  label?: string;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  ref?: React.Ref<HTMLInputElement>;
  required?: boolean;
  sx?: SxProps;
  type?: 'email' | 'hidden' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url';
  value?: string;
  variant?: 'filled' | 'outlined' | 'standard' | 'unstyled';
  InputProps?: InputProps;
  slotProps?: TextFieldProps['slotProps'];
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>) => void;
  helperText?: string;
  autoComplete?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  size?: TextFieldProps['size'];
  onClick?: TextFieldProps['onClick'];
};

/**
 * TextInput component, part of Delogue design system.
 * @param autoFocus - whether the input should automatically receive focus when the component mounts
 * @param color - color theme of the input, default is 'primary'
 * @param defaultValue - default value for uncontrolled input
 * @param disabled - whether the input is disabled
 * @param error - whether the input is in an error state
 * @param fullWidth - whether the input should take up the full width of its container
 * @param id - unique identifier for the input element
 * @param label - label text to display above the input
 * @param name - name attribute for the input element, used for form submission
 * @param onChange - callback function to be called when the input value changes
 * @param placeholder - placeholder text to display when the input is empty
 * @param required - whether the input is required, will show a * after the label
 * @param sx - MUI sx prop for custom styling
 * @param type - type of input, default is 'text', can also be 'password', other types are not optimized for this component
 * @param value - controlled value for the input
 * @param variant - visual variant of the input, either 'filled', 'outlined', 'standard', or 'unstyled', default is 'standard'
 * @param InputProps - additional props to pass to the underlying Input component
 * @param onKeyDown - callback function to be called when a key is pressed
 * @param onPaste - callback function to be called when content is pasted
 * @param helperText - helper text to display below the input, will be displayed in red if the input is in an error state
 * @param autoComplete - autocomplete attribute for the input, used for autocomplete suggestions
 * @param onBlur - callback function to be called when the input loses focus
 * @param onFocus - callback function to be called when the input receives focus
 * @param size - size of the input, either 'small' or 'medium'
 * @param onClick - callback function to be called when the input is clicked
 */
export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      autoFocus,
      color,
      defaultValue,
      disabled,
      error,
      fullWidth,
      id,
      label,
      name,
      onChange,
      placeholder,
      required,
      sx,
      type,
      value,
      variant = 'standard',
      helperText,
      onBlur,
      onFocus,
      onPaste,
      InputProps: inputPropsProp,
      slotProps,
      ...props
    },
    ref
  ) => {
    // Callers (Autocomplete/ComboBox renderInput) always pass plain-object slot props,
    // but MUI's SlotProps type also permits an ownerState callback — narrow to object.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- slot prop is a plain object at every call site; narrow from MUI's object|callback union
    const inputLabelSlot = slotProps?.inputLabel as Record<string, unknown> | undefined;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- slot prop is a plain object at every call site; narrow from MUI's object|callback union
    const inputSlot = slotProps?.input as Record<string, unknown> | undefined;
    return (
      <StyledTextField
        {...props}
        slotProps={{
          ...slotProps,
          inputLabel: {
            shrink: true,
            required,
            ...inputLabelSlot,
          },
          input: { ...inputPropsProp, ...inputSlot },
        }}
        autoFocus={autoFocus}
        color={color}
        defaultValue={defaultValue}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        id={id}
        label={label}
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        inputRef={ref} // Forward the ref here
        required={false}
        sx={sx}
        type={type}
        value={value}
        variant={variant === 'unstyled' ? 'standard' : variant}
        onKeyDown={props.onKeyDown}
        onPaste={onPaste}
        helperText={helperText}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    );
  }
);

TextInput.displayName = 'TextInput';

export const StyledTextField: ComponentType<TextFieldProps & CommonLabelSeparateProps> = styled(
  ({
    labelSeparate,
    errorIconProps,
    errorIcon,
    helperText: hint,
    labelVariant,
    labelWeight,
    isUnderline,
    ...styledProps
  }: TextFieldProps & CommonLabelSeparateProps) => {
    const helperText =
      hint && styledProps?.error && errorIcon ? (
        <Box
          sx={{
            p: 0,
            m: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {errorIcon} {hint}
        </Box>
      ) : (
        hint
      );
    return <TextField {...styledProps} helperText={helperText} />;
  }
)(({ theme, labelSeparate, labelVariant, isUnderline, labelWeight, labelColor, ...rest }) => {
  return {
    ...getInputStyles({
      theme,
      labelSeparate,
      variant: rest?.variant,
      isUnderline,
      labelColor,
      labelVariant,
      labelWeight,
    }),
  };
});
